import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DialogBackdrop } from '../ui/dialog-backdrop';
import styles from './Modal.module.css';

interface ModalProps {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}

export const Modal = ({ title, description, onClose, children }: ModalProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const describedBy = description ? descriptionId : undefined;

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return createPortal(
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog
        className={styles.modal}
        open
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}>
        <section className={styles.panel}>
          <header>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
            <button ref={closeButtonRef} type="button" aria-label="Close" onClick={onClose}>
              <X aria-hidden="true" />
            </button>
          </header>
          {children}
        </section>
      </dialog>
    </>,
    document.body,
  );
};
